import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Router } from '@angular/router';
import { switchMap, takeUntil } from 'rxjs';
import { LinkMappingTool } from 'src/app/enums/mapping';
import { Transaction } from 'src/app/models/transaction';
import { AuthService } from 'src/app/services/auth.service';
import { CommandeService } from 'src/app/services/commande.service';
import { TicketService } from 'src/app/services/ticket.service';
import { Subject } from 'rxjs';
import { InvoiceComponent } from '../../invoice/invoice.component';
import { generatePdf } from 'src/app/utils/utils';
import { InvoiceData, InvoiceItem } from 'src/app/models/invoice';
import { ClientInfos } from 'src/app/models/clientInfos';
import { statusKind, statusLabel } from '../../marchand/shared/tx-table.utils';

@Component({
  selector: 'app-commandes',
  templateUrl: './commandes.component.html',
  styleUrls: ['../../marchand/shared/tx-table.css', './commandes.component.css'],
})
export class CommandesComponent implements OnInit, OnDestroy {
  @Input() type: string = '';
  data: Transaction[] = []; // Toutes les données
  filteredData: Transaction[] = []; // Données filtrées
  label: any;
  idTicket: number = 0;
  clientInfos: ClientInfos | null = null;

  private unsubscribe$ = new Subject<void>(); // Utilisation d'un Subject pour se désabonner
   // --- Tableau ---
  baseData: Transaction[] = [];
  searchQuery = '';
  page = 1;
  readonly pageSize = 10;
  readonly statusKind = statusKind;
  readonly statusLabel = statusLabel;

  constructor(
    private service: CommandeService,
    private authService: AuthService,
    private ticketService: TicketService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.label = LinkMappingTool.getAvantageLabel(
      this.router.url.split('/')[2]
    );
    this.loadTicketData();
    this.loadCommandes();
    this.getUserInfos();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(); // Déclenche la désinscription
    this.unsubscribe$.complete();
  }

  private getUserInfos(): void {
    this.authService
      .getUserInfos('company')
      .pipe(
        takeUntil(this.unsubscribe$) // S'assure de se désabonner au bon moment
      )
      .subscribe((result) => {
       // console.log('Informations du client récupérées:', result.data.company);
        this.clientInfos = {
          customerId: result.data.company.customerId,
          name: result.data.company.name,
          ninea: result.data.company.ninea,
          address: result.data.company.address,
          tickets: result.data.company.tickets,
          ticketAmount: result.data.company.ticketAmount,
          ownerFirstName: result.data.company.ownerFirstName,
          ownerLastName: result.data.company.ownerLastName,
          ownerEmail: result.data.company.ownerEmail,
          ownerPhone: result.data.company.ownerPhone,
        };
      });
  }

  private loadTicketData(): void {
    this.ticketService
      .getAllTickets()
      .pipe(
        takeUntil(this.unsubscribe$) // S'assure de se désabonner au bon moment
      )
      .subscribe((re) => {
        const tickets = Array.isArray(re.tickets) ? re.tickets : [];
        const ticket = tickets.find(
          (item: { label: string }) => item.label === this.label
        );
        if (ticket) {
          this.idTicket = ticket.id;
        }
      });
  }

  private loadCommandes(): void {
    this.authService
      .getUser()
      .pipe(
        switchMap((user) => {
          const sub = user?.sub;
          return this.service.getCommandes(sub);
        }),
        takeUntil(this.unsubscribe$) // S'assure de se désabonner au bon moment
      )
      .subscribe(
        (commandes) => {
          this.data = commandes;
         this.baseData = this.filterCommandes(commandes);
         this.filteredData = this.baseData;
        },
        (error) => console.error(error)
      );
  }

  private filterCommandes(commandes: Transaction[]): Transaction[] {
    return commandes.filter(
      (item) => item.type === this.type && item.ticketId === String(this.idTicket)
    );
  }

   filterTable(event: Event): void {
    this.searchQuery = (event.target as HTMLInputElement).value;
    const q = this.searchQuery.trim().toLowerCase();
    this.filteredData = !q
      ? this.baseData
      : this.baseData.filter((item) =>
          [item.id, item.to, item.date, item.amount, item.status, statusLabel(item.status)]
            .filter((v) => v !== undefined && v !== null)
            .join(' ').toLowerCase().includes(q),
        );
    this.page = 1;
  }

  get isTransfer(): boolean { return this.type === 'Transfer'; }
  get showInvoice(): boolean { return !this.isTransfer; }

  get idTitle(): string {
    if (this.type === 'Transfer') return 'Numéro de transaction';
    if (this.type === 'Burn') return 'Numéro de remboursement';
    return 'Numéro de commande';
  }

  get emptyText(): string {
    if (this.type === 'Transfer') return 'Aucune transaction trouvée.';
    if (this.type === 'Burn') return 'Aucun remboursement trouvé.';
    return 'Aucune commande trouvée.';
  }

  get totalPages(): number { return Math.max(1, Math.ceil(this.filteredData.length / this.pageSize)); }
  get pages(): number[] { return Array.from({ length: this.totalPages }, (_, i) => i + 1); }
  get pagedData(): Transaction[] {
    const start = (this.page - 1) * this.pageSize;
    return this.filteredData.slice(start, start + this.pageSize);
  }
  get rangeStart(): number { return this.filteredData.length ? (this.page - 1) * this.pageSize + 1 : 0; }
  get rangeEnd(): number { return Math.min(this.page * this.pageSize, this.filteredData.length); }
  goTo(page: number): void { this.page = Math.min(Math.max(1, page), this.totalPages); }

  /** La facture n'est téléchargeable que pour une commande confirmée */
  canDownload(row: Transaction): boolean { return statusKind(row.status) === 'success'; }

  onDownload(row: Transaction): void {
    if (this.canDownload(row) && this.clientInfos) this.handleDownload(row);
  }

  /** 30000 -> "30 000" */
  formatNumber(value: any): string {
    const n = Number(value);
    if (value === null || value === undefined || isNaN(n)) return value ?? '—';
    return n.toLocaleString('fr-FR', { maximumFractionDigits: 0 }).replace(/\u202f|\u00a0/g, ' ');
  }

  /** "2026-09-30T09:53:00Z" -> "2026-09-30" ; les autres formats restent tels quels */
  formatDate(value: any): string {
    if (!value) return '—';
    const str = String(value);
    return /^\d{4}-\d{2}-\d{2}/.test(str) ? str.slice(0, 10) : str;
  }

  trackById(_: number, row: Transaction): string { return row.id; }

 
  handleDownload(rowData: any) {
   // console.log('Téléchargement PDF pour:', rowData);
    const items: InvoiceItem[] = [];
    const qty = rowData.amount / this.clientInfos!.ticketAmount;
    items.push({
      description: this.label,
      quantity: qty,
      unitPrice: this.clientInfos!.ticketAmount,
    });
    const data: InvoiceData = {
      customerId: this.clientInfos!.customerId,
      customerName: this.clientInfos!.name,
      customerNinea: this.clientInfos!.ninea,
      customerAddress: this.clientInfos!.address,
      customerPhone: this.clientInfos!.ownerPhone,
      invoiceNumber: rowData.id,
      date: rowData.date,
      items: items,
      totalAmount: rowData.amount,
    };
    //console.log(data);
    generatePdf(data);
  }

  // generatePdf(data: any) {
  //   const doc = new jsPDF();

  //   const logo = new Image();
  //   logo.src = 'assets/logo.png';

  //   logo.onload = () => {
  //     // 1. Logo (haut gauche)
  //     doc.addImage(logo, 'PNG', 10, 10, 30, 20);

  //     // 3. Grand titre centré sous le logo
  //     doc.setFontSize(18);
  //     doc.text('FACTURE', 105, 40, { align: 'center' });

  //     // 4. Ligne de séparation
  //     doc.setLineWidth(0.5);
  //     doc.line(10, 45, 200, 45);

  //     // 5. Infos facturation (en bas du titre)
  //     let y = 60;
  //     doc.setFontSize(12);
  //     doc.setFont('helvetica', 'normal');
  //     doc.text(`Facture n°: ${data.id}`, 10, y);
  //     doc.text(`Date: ${new Date(data.date).toLocaleDateString()}`, 10, y + 6);
  //     doc.text(`Client: ${data.to}`, 10, y + 12);
  //     doc.text(`Adresse: 3 avenue Geneviève de Galard`, 10, y + 18);
  //     doc.text(`Téléphone: 0663542587`, 10, y + 24);

  //     // 6. Tableau produit (exemple simplifié)
  //     y += 40;
  //     doc.setFont('helvetica', 'bold');
  //     doc.text('Description', 10, y);
  //     doc.text('Quantité', 100, y);
  //     doc.text('Prix unitaire', 130, y);
  //     doc.text('Total', 170, y);
  //     y += 6;
  //     doc.line(10, y, 200, y);
  //     y += 10;

  //     doc.setFont('helvetica', 'normal');
  //     doc.text('Transfert vers bénéficiaire', 10, y);
  //     doc.text('1', 105, y);
  //     doc.text(`${data.amount} €`, 135, y);
  //     doc.text(`${data.amount} €`, 175, y);

  //     // 7. Total
  //     y += 15;
  //     doc.setFontSize(14);
  //     doc.setFont('helvetica', 'bold');
  //     doc.text(`Total : ${data.amount} €`, 170, y, { align: 'right' });

  //     // 8. Footer
  //     doc.setFontSize(10);
  //     doc.setFont('helvetica', 'normal');
  //     doc.setTextColor(100);
  //     doc.text('Merci pour votre confiance.', 105, 280, { align: 'center' });

  //     // 9. Sauvegarde
  //     doc.save(`facture-${data.id}.pdf`);
  //   };
  // }
}
